import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import { EmptyState, SkeletonSettings } from '@/components/ui/hayat';
import axios from 'axios';

/**
 * Phase 16: Household Settings & Admin Management
 * Central hub for all admin functions
 */
function HouseholdSettingsPage() {
  const { familyUser, household, getAuthHeader, isAdmin } = useFamilyAuth();
  const [loading, setLoading] = useState(true);
  const [householdData, setHouseholdData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (household) {
      loadHouseholdData();
    }
  }, [household]);

  const loadHouseholdData = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/households/${household.id}`,
        { headers: getAuthHeader() }
      );
      setHouseholdData(response.data);
    } catch (error) {
      console.error('Error loading household:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Möchten Sie dieses Mitglied wirklich entfernen?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/family/${userId}`,
        { headers: getAuthHeader() }
      );
      await loadHouseholdData();
      alert('Mitglied erfolgreich entfernt.');
    } catch (error) {
      console.error('Error removing member:', error);
      alert(error.response?.data?.detail || 'Fehler beim Entfernen des Mitglieds.');
    }
  };

  const handleChangeRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    
    if (!window.confirm(`Rolle zu "${newRole === 'admin' ? 'Admin' : 'Mitglied'}" ändern?`)) {
      return;
    }

    try {
      await axios.put(
        `${BACKEND_URL}/api/family/${userId}/role`,
        { role: newRole },
        { headers: getAuthHeader() }
      );
      await loadHouseholdData();
      alert('Rolle erfolgreich geändert.');
    } catch (error) {
      console.error('Error changing role:', error);
      alert(error.response?.data?.detail || 'Fehler beim Ändern der Rolle.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] pb-24 page-transition">
        <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-skeleton-pulse bg-neutral-700 h-10 w-64 rounded-2xl"></div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonSettings />
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: '📊' },
    { id: 'members', label: 'Mitglieder', icon: '👥', count: householdData?.family_members?.length },
    { id: 'seniors', label: 'Senioren', icon: '👴', count: householdData?.seniors?.length },
    { id: 'devices', label: 'Geräte', icon: '📱', count: householdData?.devices?.length }
  ];

  return (
    <div className="min-h-screen bg-[#050816] pb-24 page-transition">
      {/* Header */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40 glass-effect">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-fade-in-down">
            <h1 className="text-3xl font-bold text-[#F9FAFB] mb-1">
              Haushalt verwalten ⚙️
            </h1>
            <p className="text-neutral-500 font-medium">{householdData?.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-6 py-3 rounded-2xl font-semibold text-sm whitespace-nowrap
                transition-all duration-300 ease-spring-soft flex items-center gap-2
                ${activeTab === tab.id
                  ? 'bg-[#5B4B8A] text-white shadow-hayat-lg scale-105'
                  : 'bg-neutral-800/50 text-[#CBD5F5] hover:bg-neutral-800 shadow-md border border-neutral-600/40 hover:scale-105'
                }
              `}
            >
              <span className="text-lg">{tab.icon}</span>
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-neutral-700'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-8 shadow-hayat-md border border-neutral-600/40">
              <h2 className="text-2xl font-bold text-[#F9FAFB] mb-6 flex items-center gap-2">
                <span>🚀</span>
                Schnellaktionen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Invite Member */}
                {isAdmin && (
                  <button
                    onClick={() => navigate('/family/invite')}
                    className="p-6 bg-gradient-to-br from-[#5B4B8A] to-[#4a3d72] text-white rounded-2xl hover:shadow-hayat-xl hover:scale-105 transition-all duration-300 text-left"
                  >
                    <div className="text-4xl mb-3">👥</div>
                    <h3 className="text-lg font-bold mb-1">Mitglied einladen</h3>
                    <p className="text-sm opacity-90">Neues Familienmitglied hinzufügen</p>
                  </button>
                )}

                {/* Add Senior (Link to Caregiver Dashboard) */}
                <button
                  onClick={() => window.open('/dashboard', '_blank')}
                  className="p-6 bg-gradient-to-br from-[#6DCEE8] to-[#3a9dbe] text-white rounded-2xl hover:shadow-hayat-xl hover:scale-105 transition-all duration-300 text-left"
                >
                  <div className="text-4xl mb-3">👴</div>
                  <h3 className="text-lg font-bold mb-1">Senior hinzufügen</h3>
                  <p className="text-sm opacity-90">Neuen Senior-Profil anlegen</p>
                </button>

                {/* Connect Device (Link to Caregiver Dashboard) */}
                <button
                  onClick={() => window.open('/dashboard', '_blank')}
                  className="p-6 bg-gradient-to-br from-[#F6B352] to-[#d49640] text-white rounded-2xl hover:shadow-hayat-xl hover:scale-105 transition-all duration-300 text-left"
                >
                  <div className="text-4xl mb-3">📱</div>
                  <h3 className="text-lg font-bold mb-1">Gerät verbinden</h3>
                  <p className="text-sm opacity-90">Tablet mit Senior verknüpfen</p>
                </button>

                {/* View Seniors */}
                <button
                  onClick={() => navigate('/family/seniors')}
                  className="p-6 bg-neutral-800/50 border-2 border-neutral-600/40 rounded-2xl hover:shadow-hayat-lg hover:scale-105 transition-all duration-300 text-left hover:border-[#5B4B8A]"
                >
                  <div className="text-4xl mb-3">👨‍👩‍👧</div>
                  <h3 className="text-lg font-bold text-[#F9FAFB] mb-1">Senioren ansehen</h3>
                  <p className="text-sm text-neutral-500">Alle Profile verwalten</p>
                </button>

                {/* Activity */}
                <button
                  onClick={() => navigate('/family/activity')}
                  className="p-6 bg-neutral-800/50 border-2 border-neutral-600/40 rounded-2xl hover:shadow-hayat-lg hover:scale-105 transition-all duration-300 text-left hover:border-[#5B4B8A]"
                >
                  <div className="text-4xl mb-3">📊</div>
                  <h3 className="text-lg font-bold text-[#F9FAFB] mb-1">Aktivitäten</h3>
                  <p className="text-sm text-neutral-500">Ereignisse einsehen</p>
                </button>

                {/* Settings */}
                <button
                  onClick={() => setActiveTab('members')}
                  className="p-6 bg-neutral-800/50 border-2 border-neutral-600/40 rounded-2xl hover:shadow-hayat-lg hover:scale-105 transition-all duration-300 text-left hover:border-[#5B4B8A]"
                >
                  <div className="text-4xl mb-3">⚙️</div>
                  <h3 className="text-lg font-bold text-[#F9FAFB] mb-1">Einstellungen</h3>
                  <p className="text-sm text-neutral-500">Haushalt verwalten</p>
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5B4B8A] to-[#4a3d72] flex items-center justify-center text-3xl">
                    👥
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#F9FAFB]">{householdData?.family_members?.length || 0}</p>
                    <p className="text-sm text-neutral-500">Familienmitglieder</p>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6DCEE8] to-[#3a9dbe] flex items-center justify-center text-3xl">
                    👴
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#F9FAFB]">{householdData?.seniors?.length || 0}</p>
                    <p className="text-sm text-neutral-500">Senioren</p>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md border border-neutral-600/40">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F6B352] to-[#d49640] flex items-center justify-center text-3xl">
                    📱
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#F9FAFB]">{householdData?.devices?.length || 0}</p>
                    <p className="text-sm text-neutral-500">Geräte</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            {/* Add Member Button */}
            {isAdmin && (
              <button
                onClick={() => navigate('/family/invite')}
                className="w-full p-6 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-3xl hover:shadow-hayat-xl transition-all font-bold flex items-center justify-center gap-3 hover:scale-105 active:scale-95"
              >
                <span className="text-2xl">➕</span>
                Neues Mitglied einladen
              </button>
            )}

            {/* Members List */}
            {householdData?.family_members?.length === 0 ? (
              <EmptyState
                icon="👥"
                title="Keine Mitglieder"
                description="Laden Sie Familienmitglieder ein, um gemeinsam zu verwalten."
              />
            ) : (
              <div className="space-y-4">
                {householdData?.family_members?.map((member, idx) => (
                  <div
                    key={member.id}
                    className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md hover:shadow-hayat-lg transition-all border border-neutral-600/40 animate-scale-in"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5B4B8A] to-[#4a3d72] flex items-center justify-center text-white text-2xl font-bold">
                          {member.name.charAt(0)}
                        </div>

                        {/* Info */}
                        <div>
                          <h3 className="text-lg font-bold text-[#F9FAFB]">{member.name}</h3>
                          <p className="text-sm text-neutral-500">{member.email}</p>
                          <div className="flex gap-2 mt-2">
                            {/* Role Badge */}
                            <span className={`px-3 py-1 rounded-xl text-xs font-bold ${
                              member.role === 'admin' 
                                ? 'bg-[#22C55E]/20 text-[#22C55E]'
                                : 'bg-neutral-700 text-[#CBD5F5]'
                            }`}>
                              {member.role === 'admin' ? '🔑 Admin' : '👤 Mitglied'}
                            </span>

                            {/* Status Badge */}
                            <span className={`px-3 py-1 rounded-xl text-xs font-bold ${
                              member.status === 'active' 
                                ? 'bg-[#22C55E]/20 text-[#22C55E]'
                                : member.status === 'pending'
                                ? 'bg-[#F6B352]/20 text-[#F6B352]'
                                : 'bg-[#EF4444]/20 text-[#EF4444]'
                            }`}>
                              {member.status === 'active' && '✓ Aktiv'}
                              {member.status === 'pending' && '⏱ Ausstehend'}
                              {member.status === 'removed' && '✕ Entfernt'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions (Admin only) */}
                      {isAdmin && member.id !== familyUser.id && member.status === 'active' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleChangeRole(member.id, member.role)}
                            className="px-4 py-2 bg-[#6DCEE8]/20 text-[#6DCEE8] rounded-xl hover:bg-[#6DCEE8]/30 transition-colors font-semibold text-sm"
                            title="Rolle ändern"
                          >
                            {member.role === 'admin' ? '↓ Zu Mitglied' : '↑ Zu Admin'}
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="px-4 py-2 bg-[#EF4444]/20 text-[#EF4444] rounded-xl hover:bg-[#EF4444]/30 transition-colors font-semibold text-sm"
                            title="Entfernen"
                          >
                            Entfernen
                          </button>
                        </div>
                      )}

                      {/* Self indicator */}
                      {member.id === familyUser.id && (
                        <span className="px-4 py-2 bg-[#5B4B8A]/20 text-[#5B4B8A] rounded-xl font-semibold text-sm">
                          Sie
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Seniors Tab */}
        {activeTab === 'seniors' && (
          <div className="space-y-6">
            <div className="bg-[#6DCEE8]/10 border border-[#6DCEE8]/40 px-6 py-4 rounded-2xl flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <p className="font-bold text-[#F9FAFB] mb-1">Senioren-Verwaltung</p>
                <p className="text-sm text-[#6DCEE8]">
                  Senioren werden über das Caregiver-Dashboard verwaltet. <button onClick={() => window.open('/dashboard', '_blank')} className="underline font-semibold">Zum Dashboard →</button>
                </p>
              </div>
            </div>

            {householdData?.seniors?.map((senior, idx) => (
              <div
                key={senior.id}
                onClick={() => navigate(`/family/seniors/${senior.id}`)}
                className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md hover:shadow-hayat-lg transition-all border border-neutral-600/40 cursor-pointer animate-scale-in"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6DCEE8] to-[#3a9dbe] flex items-center justify-center text-white text-2xl font-bold">
                    {senior.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#F9FAFB]">{senior.name}</h3>
                    <p className="text-sm text-neutral-500">
                      {senior.language === 'de' && '🇩🇪 Deutsch'}
                      {senior.language === 'tr' && '🇹🇷 Türkisch'}
                      {senior.language === 'ar' && '🇸🇦 Arabisch'}
                    </p>
                  </div>
                  <span className="text-2xl text-neutral-500">›</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <div className="space-y-6">
            <div className="bg-[#F6B352]/10 border border-[#F6B352]/40 px-6 py-4 rounded-2xl flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <p className="font-bold text-[#F9FAFB] mb-1">Geräte-Verwaltung</p>
                <p className="text-sm text-[#F6B352]">
                  Geräte werden über das Caregiver-Dashboard verwaltet. <button onClick={() => window.open('/dashboard', '_blank')} className="underline font-semibold">Zum Dashboard →</button>
                </p>
              </div>
            </div>

            {householdData?.devices?.map((device, idx) => (
              <div
                key={device.id}
                className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md hover:shadow-hayat-lg transition-all border border-neutral-600/40 animate-scale-in"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F6B352] to-[#d49640] flex items-center justify-center text-3xl">
                    📱
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#F9FAFB]">{device.serial_number}</h3>
                    <p className="text-sm text-neutral-500">
                      Status: <span className={`font-semibold ${device.status === 'online' ? 'text-[#22C55E]' : 'text-neutral-500'}`}>
                        {device.status === 'online' ? '✅ Online' : '🔴 Offline'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HouseholdSettingsPage;
